// Web Entry Point - WebSocket transport only
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import App from './App';
import { store } from './store';
import { getTransport, resetTransport } from './transport';
import { brandService } from './services/brand';
import './index.css';
import './web-styles.css';

declare global {
  interface Window {
    __APP_MODE__?: 'dev' | 'demo';
  }
}

const API_URL = import.meta.env.VITE_API_URL || window.location.origin;
const WS_URL = import.meta.env.VITE_WS_URL ||
  (window.location.protocol === 'https:'
    ? `wss://${window.location.host}/socket`
    : `ws://${window.location.host}/socket`);

let currentToken: string | null = localStorage.getItem('auth_token');

async function initializeTransport(): Promise<boolean> {
  resetTransport();
  if (currentToken) {
    try {
      const transport = getTransport({
        serverUrl: WS_URL,
        token: currentToken,
      });
      await transport.connect?.();
      console.log('[Web] WebSocket connected');

      const configResult = await transport.getConfig();
      if (configResult.success && configResult.data?.config) {
        const config = configResult.data.config;
        if (!config.workingDirectory) {
          await transport.setConfig({ workingDirectory: '/tmp/mednnect-ai' });
          console.log('[Web] Set default working directory: /tmp/mednnect-ai');
        }
      }
      return true;
    } catch (error) {
      console.error('[Web] Failed to connect:', error);
      localStorage.removeItem('auth_token');
      currentToken = null;
      return false;
    }
  }
  return false;
}

async function checkAuth(): Promise<boolean> {
  if (!currentToken) return false;
  try {
    const response = await fetch(`${API_URL}/api/auth/me`, {
      headers: { 'Authorization': `Bearer ${currentToken}` },
    });
    if (response.ok) return true;
    localStorage.removeItem('auth_token');
    currentToken = null;
    return false;
  } catch {
    return false;
  }
}

function loginShell(title: string, innerHtml: string): string {
  const demoWarning = window.__APP_MODE__ === 'demo' ? `
    <div class="demo-warning-banner" role="alert">
      <span class="demo-warning-icon">⚠️</span>
      <div class="demo-warning-content">
        <div class="demo-warning-title">Demo Version — Do Not Enter Sensitive Data</div>
        <div>This is a public demo of ${brandService.getName()}. Do not input sensitive personal, health, or medical information. Data is not protected and may be visible to others.</div>
      </div>
    </div>
  ` : '';
  return `
    <div class="login-container">
      <div class="login-box">
        <h1 class="login-title">${title}</h1>
        ${demoWarning}
        ${innerHtml}
      </div>
    </div>
  `;
}

function getHashRoute(): string {
  const hash = window.location.hash.replace(/^#\/?/, '');
  return hash.split('?')[0] || 'login';
}

function showLoginPage() {
  const root = document.getElementById('root');
  if (!root) return;

  root.innerHTML = loginShell(brandService.getName(), `
    <form id="login-form" class="login-form">
      <div class="login-field">
        <label for="email">Email</label>
        <input type="email" id="email" name="email" required placeholder="Enter your email" />
      </div>
      <div class="login-field">
        <label for="password">Password</label>
        <input type="password" id="password" name="password" required placeholder="Enter your password" />
      </div>
      <button type="submit" class="login-button">Sign In</button>
      <div id="login-error" class="login-error"></div>
    </form>
    <div class="login-divider">or</div>
    <button id="register-button" class="login-button secondary">Create Account</button>
    <button id="guest-button" class="login-button guest">Continue as Guest</button>
    <a class="login-link" id="forgot-password-link">Forgot password?</a>
  `);

  document.getElementById('login-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (response.ok && data.token) {
        localStorage.setItem('auth_token', data.token);
        currentToken = data.token;
        window.location.hash = '';
        window.location.reload();
      } else {
        document.getElementById('login-error')!.textContent = data.error || 'Login failed';
      }
    } catch {
      document.getElementById('login-error')!.textContent = 'Connection error. Please try again.';
    }
  });

  document.getElementById('register-button')?.addEventListener('click', async () => {
    const email = (document.getElementById('email') as HTMLInputElement)?.value;
    const password = (document.getElementById('password') as HTMLInputElement)?.value;
    if (!email || !password) {
      document.getElementById('login-error')!.textContent = 'Please enter email and password';
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (response.ok && data.success && data.requiresVerification) {
        // Carry userId + code to the verify-code page via sessionStorage.
        sessionStorage.setItem('reg_user_id', data.userId);
        if (data.verificationCode) {
          sessionStorage.setItem('reg_code', data.verificationCode);
        }
        sessionStorage.setItem('reg_email_hint', data.emailHint || '');
        window.location.hash = '#verify-code';
        window.location.reload();
      } else {
        document.getElementById('login-error')!.textContent = data.error || 'Registration failed';
      }
    } catch {
      document.getElementById('login-error')!.textContent = 'Connection error. Please try again.';
    }
  });

  document.getElementById('guest-button')?.addEventListener('click', async () => {
    const errorEl = document.getElementById('login-error')!;
    const guestButton = document.getElementById('guest-button') as HTMLButtonElement | null;
    if (guestButton) {
      guestButton.disabled = true;
      guestButton.textContent = 'Signing in...';
    }
    errorEl.textContent = '';
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: 'guest@company.ai', password: 'guest@1234' }),
      });
      const data = await response.json();
      if (response.ok && data.token) {
        localStorage.setItem('auth_token', data.token);
        currentToken = data.token;
        window.location.hash = '';
        window.location.reload();
      } else {
        errorEl.textContent = data.error || 'Guest login failed';
        if (guestButton) {
          guestButton.disabled = false;
          guestButton.textContent = 'Continue as Guest';
        }
      }
    } catch {
      errorEl.textContent = 'Connection error. Please try again.';
      if (guestButton) {
        guestButton.disabled = false;
        guestButton.textContent = 'Continue as Guest';
      }
    }
  });

  document.getElementById('forgot-password-link')?.addEventListener('click', () => {
    window.location.hash = '#forgot-password';
    window.location.reload();
  });
}

function showVerifyCodePage() {
  const root = document.getElementById('root');
  if (!root) return;

  const userId = sessionStorage.getItem('reg_user_id') || '';
  const code = sessionStorage.getItem('reg_code') || '';
  const emailHint = sessionStorage.getItem('reg_email_hint') || '';

  if (!userId) {
    window.location.hash = '';
    window.location.reload();
    return;
  }

  root.innerHTML = loginShell('Verify Your Email', `
    <p class="login-hint">A verification code was sent to ${emailHint || 'your email'}.</p>
    ${code ? `<p class="login-hint">Your code (dev): <strong>${code}</strong></p>` : ''}
    <form id="verify-form" class="login-form">
      <div class="login-field">
        <label for="code">Verification Code</label>
        <input type="text" id="code" name="code" required maxlength="6" placeholder="6-digit code" value="${code}" />
      </div>
      <button type="submit" class="login-button">Verify</button>
      <div id="verify-error" class="login-error"></div>
    </form>
    <a class="login-link" id="resend-link">Resend code</a>
    <a class="login-link" id="back-login-link">Back to login</a>
  `);

  document.getElementById('verify-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const verifyCode = formData.get('code') as string;
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, code: verifyCode }),
      });
      const data = await response.json();
      if (response.ok && data.token) {
        localStorage.setItem('auth_token', data.token);
        sessionStorage.removeItem('reg_user_id');
        sessionStorage.removeItem('reg_code');
        sessionStorage.removeItem('reg_email_hint');
        window.location.hash = '';
        window.location.reload();
      } else {
        document.getElementById('verify-error')!.textContent = data.error || 'Verification failed';
      }
    } catch {
      document.getElementById('verify-error')!.textContent = 'Connection error. Please try again.';
    }
  });

  document.getElementById('resend-link')?.addEventListener('click', async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        if (data.verificationCode) {
          sessionStorage.setItem('reg_code', data.verificationCode);
        }
        document.getElementById('verify-error')!.textContent = 'Code resent.';
        document.getElementById('verify-error')!.style.color = '#10b981';
        window.location.reload();
      } else {
        document.getElementById('verify-error')!.textContent = data.error || 'Resend failed';
      }
    } catch {
      document.getElementById('verify-error')!.textContent = 'Connection error. Please try again.';
    }
  });

  document.getElementById('back-login-link')?.addEventListener('click', () => {
    window.location.hash = '';
    window.location.reload();
  });
}

function showForgotPasswordPage() {
  const root = document.getElementById('root');
  if (!root) return;

  root.innerHTML = loginShell('Forgot Password', `
    <p class="login-hint">Enter your email to receive a reset code.</p>
    <form id="forgot-form" class="login-form">
      <div class="login-field">
        <label for="email">Email</label>
        <input type="email" id="email" name="email" required placeholder="Enter your email" />
      </div>
      <button type="submit" class="login-button">Send Reset Code</button>
      <div id="forgot-error" class="login-error"></div>
    </form>
    <a class="login-link" id="to-reset-link">Already have a code? Reset password</a>
    <a class="login-link" id="back-login-link">Back to login</a>
  `);

  document.getElementById('forgot-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const email = formData.get('email') as string;
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        // success without verificationCode means the email is not registered.
        // The backend omits the code on purpose to avoid leaking account
        // existence, so we must not redirect — stay here and inform the user.
        if (data.verificationCode) {
          sessionStorage.setItem('reset_email', email);
          sessionStorage.setItem('reset_code', data.verificationCode);
          document.getElementById('forgot-error')!.textContent = 'A reset code was sent to your email.';
          document.getElementById('forgot-error')!.style.color = '#10b981';
          setTimeout(() => {
            window.location.hash = '#reset-password';
            window.location.reload();
          }, 1200);
        } else {
          document.getElementById('forgot-error')!.textContent =
            'No account found with that email. Please check the address and try again.';
          document.getElementById('forgot-error')!.style.color = '';
        }
      } else {
        document.getElementById('forgot-error')!.textContent = data.error || 'Request failed';
      }
    } catch {
      document.getElementById('forgot-error')!.textContent = 'Connection error. Please try again.';
    }
  });

  document.getElementById('to-reset-link')?.addEventListener('click', () => {
    window.location.hash = '#reset-password';
    window.location.reload();
  });

  document.getElementById('back-login-link')?.addEventListener('click', () => {
    window.location.hash = '';
    window.location.reload();
  });
}

function showResetPasswordPage() {
  const root = document.getElementById('root');
  if (!root) return;

  const email = sessionStorage.getItem('reset_email') || '';
  const code = sessionStorage.getItem('reset_code') || '';

  root.innerHTML = loginShell('Reset Password', `
    <form id="reset-form" class="login-form">
      <div class="login-field">
        <label for="email">Email</label>
        <input type="email" id="email" name="email" required placeholder="Enter your email" value="${email}" />
      </div>
      <div class="login-field">
        <label for="code">Reset Code</label>
        <input type="text" id="code" name="code" required maxlength="6" placeholder="6-digit code" value="${code}" />
      </div>
      <div class="login-field">
        <label for="newPassword">New Password</label>
        <input type="password" id="newPassword" name="newPassword" required placeholder="At least 8 characters" />
      </div>
      <button type="submit" class="login-button">Reset Password</button>
      <div id="reset-error" class="login-error"></div>
    </form>
    <a class="login-link" id="back-login-link">Back to login</a>
  `);

  document.getElementById('reset-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const resetEmail = formData.get('email') as string;
    const resetCode = formData.get('code') as string;
    const newPassword = formData.get('newPassword') as string;
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail, code: resetCode, newPassword }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        sessionStorage.removeItem('reset_email');
        sessionStorage.removeItem('reset_code');
        document.getElementById('reset-error')!.textContent = 'Password reset. Redirecting to login...';
        document.getElementById('reset-error')!.style.color = '#10b981';
        setTimeout(() => {
          window.location.hash = '';
          window.location.reload();
        }, 1200);
      } else {
        document.getElementById('reset-error')!.textContent = data.error || 'Reset failed';
      }
    } catch {
      document.getElementById('reset-error')!.textContent = 'Connection error. Please try again.';
    }
  });

  document.getElementById('back-login-link')?.addEventListener('click', () => {
    window.location.hash = '';
    window.location.reload();
  });
}

function showAuthPage() {
  const route = getHashRoute();
  switch (route) {
    case 'verify-code':
      showVerifyCodePage();
      break;
    case 'forgot-password':
      showForgotPasswordPage();
      break;
    case 'reset-password':
      showResetPasswordPage();
      break;
    default:
      showLoginPage();
  }
}

async function main() {
  // Redirect mobile devices to /mobile
  const isMobile = /Android|iPhone|iPad|iPod|Mobile|BlackBerry|Opera Mini|IEMobile/i.test(navigator.userAgent);
  const isMobilePath = window.location.pathname === '/mobile' || window.location.pathname.startsWith('/mobile/');
  if (isMobile && !isMobilePath) {
    window.location.href = '/mobile';
    return;
  }

  const route = getHashRoute();

  // Auth pages are shown when unauthenticated, keyed by hash route.
  // The verify-code / forgot-password / reset-password routes bypass the
  // auth check so deep-links work before login.
  const isAuthPageRoute = ['verify-code', 'forgot-password', 'reset-password'].includes(route);

  if (!isAuthPageRoute) {
    const isAuthenticated = await checkAuth();
    if (!isAuthenticated) {
      showLoginPage();
      return;
    }

    const connected = await initializeTransport();
    if (!connected) {
      showLoginPage();
      return;
    }
  } else {
    showAuthPage();
    return;
  }

  (window as any).__WEB_MODE__ = true;

  const root = document.getElementById('root');
  if (!root) throw new Error('Root element not found');

  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <Provider store={store}>
        <App />
      </Provider>
    </React.StrictMode>
  );
}

main().catch((error) => {
  console.error('Failed to initialize:', error);
  showAuthPage();
});